
import * as PIXI from 'pixi.js';
import {BigWinConstants} from '@gongxigames/framework/slot/modules/bigwin/BigWinConstants';
import {BLEND_MODES, filters, Sprite, Text, Texture} from 'pixi.js';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {
    CoreDI,
    CurrencyFormatter, Easing, EmitterV5,
    FullScreenFader, inject,
    Meter, MovieClip, ObjectBuilder,
    ResolutionManager, ScreenParameters,
    Spine,
    Timer, Tween,
    UpdateManager
} from '@gongxigames/framework/core';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {GameConfig, IReelsScaleService} from '@gongxigames/framework/slot/types';
import {View} from '@gongxigames/framework/slot';

export class BigWinView extends View<GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('BigWinView');
    public static layer = LAYERS.POPUPS;

    public static readonly VIEW_BIG_WIN_FINISHED: string = 'VIEW_BIG_WIN_FINISHED';
    public static readonly VIEW_POST_FEATURE_BW_SKIPPED: string = 'VIEW_BIG_WIN_SKIPPED';
    public static readonly METER_STARTED: string = 'METER_STARTED';
    public static readonly METER_FINISHED: string = 'METER_FINISHED';
    public static readonly METER_FINAL_VALUE: string = 'SNAP_WIN_METER_TO_FINAL_VALUE';
    public static readonly METER_SET: string = 'METER_SET';

    @inject(CoreDI.ResolutionManager)
    protected _resolutionManager!: ResolutionManager;

    @inject(CoreDI.CurrencyFormatter)
    protected _currencyFormatter!: CurrencyFormatter;

    @inject(DI.ReelsScaleService)
    protected _reelsScaleService!: IReelsScaleService;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    protected _spineContainer!: Sprite;
    protected _particlesContainer!: Sprite;
    protected _particles!: Sprite;
    protected _fader!: FullScreenFader;
    protected _spine!: Spine;

    protected _emitter?: EmitterV5;
    protected _finalEmitter?: EmitterV5;

    protected _BWType!: string;

    protected _updater: UpdateManager = new UpdateManager(true);

    protected _meter?: Meter;
    protected _from!: number;
    protected _to!: number;

    protected _skipTimer?: Timer;
    protected _darkTimer?: Timer;
    protected _animationTimer?: Timer;
    protected _particlesTimer?: Timer;
    protected _finalDelayTimer?: Timer;

    protected _canSkip = false;
    protected _isSkippableImmediately = false;
    protected _isPlayWinMeter = false;
    protected _isPlaying = false;
    protected _playingSound!: string;

    public init(): void {
        super.init();

        this.updateManager.add(this._updater);

        this.initFader();
        this.initEvents();
    }

    public onResourcesLoaded(): void {
        this.initParticles();
        this.initSpineAnimation();
        this.hide();
    }

    public play(type: string, isSkippableImmediately: boolean, from: number, to: number, isPlayWinMeter: boolean): void {
        this._BWType = type;
        this._isSkippableImmediately = isSkippableImmediately;
        this._isPlayWinMeter = isPlayWinMeter;
        this._from = from;
        this._to = to;
        this._isPlaying = true;

        this.setSkipOptions();
        this.darkenBackground();
        this.playBWAnimation();
        this.startEmitParticles();

        this._playingSound = BigWinConstants.MAIN_SOUND;
        this.soundPlayer.play(this._playingSound);

        this.container.interactive = true;
        this.container.alpha = 1;
        this.show();
    }

    public skip(): void {
        if (this._isSkippableImmediately) {
            this.emit(BigWinView.VIEW_POST_FEATURE_BW_SKIPPED);
            this.stop();
        } else if (this._canSkip) {
            this.setBurstVisibility(false);
            this.setEndSound();
            this.setCounterToFinalValue();
        } else {
            return;
        }
    }

    protected setEndSound(): void {
        this.soundPlayer.stop(this._playingSound);
        this._playingSound = BigWinConstants.END_SOUND;
        this.soundPlayer.play(this._playingSound);

    }

    protected fade(): void {
        this._updater.add(new Tween(this.container)
            .to({alpha: 0}, BigWinConstants.BIG_WIN_TIMINGS.final_fade)
            .onComplete(() => this._isPlaying && this.stop())
            .start()
        );
    }

    protected stop(): void {
        this.container.interactive = false;

        this._isPlaying = false;
        this._canSkip = false;

        this.soundPlayer.stop(this._playingSound);

        this.clearTimers();
        this.stopEmitters();

        if (this._meter) {
            this._meter.onCompleteCallback = undefined;
            this._meter.stop();
        }

        this._isPlayWinMeter && this.emit(BigWinView.METER_FINISHED);

        this.setBurstVisibility(true);

        this._spine.state.clearTracks();
        this._spine.skeleton.setToSetupPose();
        this._spineContainer.visible = false;

        this.emit(BigWinView.VIEW_BIG_WIN_FINISHED);

        this.hide();
    }

    protected initEvents(): void {
        this.container.on('click', () => this.skip());
        this.container.on('tap', () => this.skip());
    }

    protected initParticles(): void {
        this._particles = this.objectBuilder.buildSprite(ObjectBuilder.resources.shared_sprite.bigWinParticles);
        this._particles.visible = false;

        this.container.addChild(this._particles);
    }

    protected initSpineAnimation(): void {
        this._spineContainer = new Sprite();

        this._spine = this.objectBuilder.buildSpine(BigWinConstants.ANIMATION_NAME);
        this._spine.x = this._gameConfig.mainFrame.width / 2;
        this._spine.y = this._gameConfig.mainFrame.height / 2;
        this._spine.start();

        this._spineContainer.addChild(this._spine);
        this._spineContainer.visible = false;

        this.container.addChild(this._spineContainer);

        this._updater.add(this._spine);

        this.addMaskOnRays();
        this.addParticlesEmitterToSlot();
    }

    protected setSkipOptions(): void {
        this._skipTimer = new Timer(() => this._canSkip = true, BigWinConstants.BIG_WIN_TIMINGS.skip_delay, true);
        this._updater.add(this._skipTimer);
    }

    protected playBWAnimation(): void {
        this._animationTimer = new Timer(() => {
            this.playSpineAnimation();
            this.startCounter();
        }, BigWinConstants.BIG_WIN_TIMINGS.animation_delay, true);
        this._updater.add(this._animationTimer);
    }

    protected startEmitParticles(): void {
        this._particlesTimer = new Timer(() => {
            this.emitParticles();
        }, BigWinConstants.BIG_WIN_TIMINGS.particles_delay, true);
        this._updater.add(this._particlesTimer);
    }

    protected startCounter(): void {
        const duration = BigWinConstants.BIG_WIN_TIMINGS.duration - BigWinConstants.BIG_WIN_TIMINGS.animation_delay;
        const counter = this._spine.skeleton.findSlot('bw_0');
        const counterText = new Text('', {
            fontFamily: 'PF DinDisplay Pro',
            fontSize: '395px',
            fontWeight: 'bold',
            fill: [
                '#f48e0c',
                '#fff88b',
                '#f9ffd5',
                '#fff88b',
                '#ffcb2a',
                '#ffcb2a',
                '#f48e0c'
            ],
            stroke: '#810000',
            strokeThickness: 30
        });

        counter.currentSprite.scale.set(1, -1);
        counter.currentSprite.texture = Texture.EMPTY;
        counter.currentSprite.removeChildren();
        counter.currentSprite.addChild(counterText);

        this._meter = new Meter(counterText, Easing.Linear.None, this._currencyFormatter);
        this._meter.from(0).to(this._to - this._from, duration);
        this._meter.start();
        this._meter.onCompleteCallback = this.onCounterFinished.bind(this);
        this._updater.add(this._meter);
        this._meter.updateCallback = () => {
            counterText.x = -counterText.getLocalBounds(counterText.parent.getBounds()).width / 2;
            counterText.y = -counterText.getLocalBounds(counterText.parent.getBounds()).height / 2;
        };

        // start flow meter
        if (this._isPlayWinMeter) {
            this.emit(BigWinView.METER_STARTED, this._from, this._to, duration);
        }
    }

    protected onCounterFinished(): void {
        if (this._meter) {
            this._meter.onCompleteCallback = undefined;
            this._meter.stop();
        }
        this.emit(BigWinView.METER_FINISHED);

        this.onBigWinAnimationCompleted();
    }

    protected onBigWinAnimationCompleted(): void {
        this._isSkippableImmediately = true;

        this.stopEmitParticles();
        this._finalDelayTimer = new Timer(() => this.fade(), BigWinConstants.BIG_WIN_TIMINGS.final_delay, true);
        this._updater.add(this._finalDelayTimer);
    }

    protected initFader(): void {
        this._fader = this._displayObjectUtils.createFullScreenFader({alpha: 0});

        this.container.addChild(this._fader);
    }

    protected addMaskOnRays(): void {
        const rays = this._spine.skeleton.findSlot('rays2');

        rays.currentSprite.mask = this._spine.skeleton.findSlot('rays_mask').currentSprite;

        if (rays.blendMode === BLEND_MODES.ADD) {
            const addFilter = new filters.AlphaFilter();

            addFilter.blendMode = BLEND_MODES.ADD;
            rays.currentSprite.filters = [addFilter];
            rays.currentSprite.blendMode = BLEND_MODES.NORMAL;
        }
    }

    protected addParticlesEmitterToSlot(): void {
        const slotSprite = this._spine.skeleton.findSlot('emitter').currentSprite;
        const sprite = new Sprite();

        sprite.anchor.set(0.5);

        slotSprite.texture = Texture.EMPTY;
        slotSprite.addChild(sprite);

        this._particlesContainer = sprite;
    }

    protected playSpineAnimation(): void {
        const startAnimationName = this.spineUtils.getAnimationNameByQuality(this._spine, `${this._BWType}_start`);
        const loopAnimationName = this.spineUtils.getAnimationNameByQuality(this._spine, `${this._BWType}_loop`);

        this._spineContainer.visible = true;

        this._spine.state.setAnimation(0, startAnimationName, false);
        this._spine.state.addAnimation(1, loopAnimationName, true, 0);
        this._spine.update(0);
    }

    protected darkenBackground(): void {
        this._fader.alpha = BigWinConstants.FADER.initial_alpha;
        this._darkTimer = new Timer(() => {
            this._updater.add(new Tween(this._fader)
                .to({alpha: BigWinConstants.FADER.alpha}, BigWinConstants.FADER.duration)
                .start()
            );
        }, BigWinConstants.FADER.delay, true);
        this._updater.add(this._darkTimer);
    }

    protected setCounterToFinalValue(): void {
        this._meter?.snapToFinalValue();
        this.emit(BigWinView.METER_FINAL_VALUE);
        this.onCounterFinished();
    }

    protected stopEmitParticles(): void {
        if (this._emitter) {
            this._emitter.stopEmit();
        }
        this.emitFinalBurst();
    }

    protected emitParticles(): void {
        const textures = this.getParticlesTextures();

        // NOTE: particles 5 ver, contains textures inside config:
        const config = BigWinConstants.PARTICLES_CONFIG(textures);

        this._emitter = new EmitterV5(
            this._particlesContainer,
            config
        );

        this._updater.add(this._emitter);
        this._emitter.startEmit();
    }

    protected emitFinalBurst(): void {
        const textures = this.getParticlesTextures();
        const config = BigWinConstants.PARTICLES_CONFIG(textures);

        config.frequency = 90;
        config.emitterLifetime = 100;
        config.particlesPerWave = 100;

        this._finalEmitter = new EmitterV5(
            this._particlesContainer,
            config
        );

        this._updater.add(this._finalEmitter);
        this._finalEmitter.startEmit();
    }

    protected getParticlesTextures(): PIXI.Texture[][] {
        const particlesTextures: PIXI.Texture[][] = [];

        for (const item of this._particles.children) {
            if (this.needAddParticleType(item as MovieClip)) {
                const particleObjects = (item as MovieClip).frames as Sprite[];

                const textures: PIXI.Texture[] = particleObjects.map(sprite => sprite.texture);

                particlesTextures.push(textures);
            }
        }

        return particlesTextures;
    }

    protected getParticleObjects(): Sprite[][] {
        const particles: Sprite[][] = [];

        for (const item of this._particles.children) {
            if (this.needAddParticleType(item as MovieClip)) {
                const particleObjects = [...(item as MovieClip).frames] as Sprite[];

                particles.push(particleObjects, particleObjects.slice().reverse());
            }
        }

        return particles;
    }

    protected needAddParticleType(item: MovieClip): boolean {
        return BigWinConstants.PARTICLE_TYPES[this._BWType].indexOf(item.name) >= 0;
    }

    protected stopEmitters(): void {
        this._emitter && this._emitter.stopEmitAndCleanup();
        this._finalEmitter && this._finalEmitter.stopEmitAndCleanup();
    }

    protected clearTimers(): void {
        this._skipTimer && this._skipTimer.stop();
        this._darkTimer && this._darkTimer.stop();
        this._animationTimer && this._animationTimer.stop();
        this._particlesTimer && this._particlesTimer.stop();
        this._finalDelayTimer && this._finalDelayTimer.stop();
    }

    protected setBurstVisibility(value: boolean): void {
        this._spine.skeleton.findSlot('burst').currentSprite.visible = value;
    }

    public onOrientationChanged(): void {
        super.onOrientationChanged();

        this._reelsScaleService.scaleByCurrentScreenOrientation(this._spineContainer);
    }

    public onResized(screenParams: ScreenParameters): void {
        this._fader?.resize(screenParams);
    }
}
